import { styled } from '@mui/material';
import { visuallyHidden } from '@mui/utils';
import { SocialType } from '../../../models/enum';
import {
  EmailShareButton,
  FacebookShareButton,
  LinkedinShareButton,
  PinterestShareButton,
  TelegramShareButton,
  TwitterShareButton,
  WhatsappShareButton,
} from 'react-share';
import { ButtonType } from './ProductDescription';
import { StyledLink } from '../../../styles/shared';

export const ProductDescriptionWrapper = styled('div')(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacings.unit(8),
}));

export const ProductTitleWrapper = styled('div')(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacings.unit(3),
}));

export const CategoryName = styled('div')(({ theme }) => ({
  color: theme.colors.mainTitleText,
  fontSize: theme.fonts.utility.size(7),
  fontWeight: 900,
  lineHeight: '125%',
  letterSpacing: theme.spacings.unit(0.49),
  textTransform: 'uppercase',
}));

export const ProductLabel = styled('div')(({ theme }) => ({
  color: theme.colors.secondaryText,
  fontSize: theme.fonts.utility.size(9),
  lineHeight: '160%',
  whiteSpace: 'break-spaces',
  fontWeight: 400,
  '@media print': {
    pageBreakInside: 'avoid',
    breakInside: 'avoid',
  },
}));

export const Description = styled('div', {
  shouldForwardProp: (prop) => !['showMoreDetail'].includes(prop as string),
})(({ theme, showMoreDetail }) => ({
  color: theme.colors.secondaryText,
  fontSize: theme.fonts.utility.size(8),
  lineHeight: '160%',
  whiteSpace: 'break-spaces',
  overflowY: 'hidden',
  maxHeight: `calc(${theme.fonts.utility.lineHeight(29 / 4)} * ${
    process.env.NEXT_PUBLIC_PRODUCT_DESCRIPTION_LESS_LINES ?? 10
  })`,
  ...(showMoreDetail && {
    maxHeight: 'fit-content',
  }),
  '@media print': {
    maxHeight: 'fit-content',
    pageBreakInside: 'avoid',
    breakInside: 'avoid',
  },
}));

export const TextWrapper = styled('div')(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacings.unit(3),
}));

export const ButtonsWrapper = styled('div')(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacings.unit(4),
  '@media print': {
    display: 'none',
  },
}));

export const StyledButton = styled('button')(({ theme, type }) => ({
  display: 'flex',
  padding: `${theme.spacings.unit(3.75)} ${theme.spacings.unit(6)}`,
  justifyContent: 'center',
  alignItems: 'center',
  gap: theme.spacings.unit(2),
  alignSelf: 'stretch',
  background:
    type === ButtonType.PRIMARY ? theme.colors.primary : theme.colors.white,
  borderRadius: theme.spacings.unit(0.75),
  border:
    type === ButtonType.PRIMARY
      ? 0
      : `${theme.spacings.unit(0.375)} solid ${theme.colors.primary}`,
  '> span': {
    color:
      type === ButtonType.PRIMARY
        ? theme.colors.white
        : theme.colors.mainTitleText,
  },
  ':hover': {
    cursor: 'pointer',
  },
}));

export const ButtonText = styled('span')(({ theme }) => ({
  color: theme.colors.white,
  textAlign: 'center',
  fontSize: theme.fonts.utility.size(7),
  fontWeight: 800,
  lineHeight: '125%',
  letterSpacing: theme.spacings.unit(0.48),
  textTransform: 'uppercase',
  flex: '1 0 0',
}));

export const CopyButtonText = styled('span')(({ theme }) => ({
  color: theme.colors.secondaryText,
  fontSize: theme.spacings.unit(3.5),
  fontWeight: 600,
  lineHeight: '160%',
}));

export const Share = styled('div')(({ theme }) => ({
  color: theme.colors.secondaryText,
  flexWrap: 'wrap',
  fontSize: theme.fonts.utility.size(5),
  display: 'flex',
  alignItems: 'center',
  flexDirection: 'row',
  gap: theme.spacings.unit(2),
  '@media print': {
    display: 'none',
  },
}));

export const SocialName = styled('span')(({ theme }) => ({
  ...visuallyHidden,
}));

export const InstagramShareButton = styled('button')(({ theme }) => ({
  border: 'none',
  backgroundColor: 'transparent',
  padding: 0,
  font: 'inherit',
  color: 'inherit',
  cursor: 'pointer',
  '& svg:hover': {
    '& path': {
      fill: `${theme.colors.primary}`,
    },
  },
}));

export const SocialBorder = styled('div')(({ theme }) => ({
  display: 'flex',
  borderRadius: theme.spacings.unit(0.25),
  border: `${theme.spacings.unit(0.25)} solid ${theme.colors.border}`,
  justifyContent: 'center',
  alignItems: 'center',
  height: 'fit-content',
  width: 'fit-content',
}));

export const Divider = styled('hr')(({ theme }) => ({
  height: theme.spacings.unit(0.25),
  alignSelf: 'stretch',
  background: theme.colors.divider,
}));

export const CopyButton = styled('button')(({ theme }) => ({
  background: 'transparent',
  border: 0,
  height: theme.spacings.unit(10),
  '& span:hover': {
    color: `${theme.colors.primary}`,
    cursor: 'pointer',
  },
}));

export const SocialLink = styled(
  ({ type, ...props }: any & { type: SocialType }) => {
    switch (type) {
      case SocialType.FACEBOOK:
        return <FacebookShareButton {...props} />;
      case SocialType.TWITTER:
        return <TwitterShareButton {...props} />;
      case SocialType.WHATSAPP:
        return <WhatsappShareButton {...props} />;
      case SocialType.TELEGRAM:
        return <TelegramShareButton {...props} />;
      case SocialType.LINKEDIN:
        return <LinkedinShareButton {...props} />;
      case SocialType.PINTEREST:
        return <PinterestShareButton {...props} />;
      case SocialType.EMAIL:
        return <EmailShareButton {...props} />;
      case SocialType.INSTAGRAM:
        return <InstagramShareButton {...props} />;
      default:
        return <></>;
    }
  }
)(({ theme }) => ({
  '& svg': {
    width: theme.spacings.unit(10),
    height: theme.spacings.unit(10),
  },
  color: `${theme.colors.secondaryText}`,
  '& svg:hover': {
    '& path': {
      fill: `${theme.colors.primary}`,
    },
  },
}));

export const ProductName = styled('div')(({ theme }) => ({
  color: theme.colors.primaryText,
  fontFeatureSettings: '"liga" off',
  fontSize: theme.fonts.utility.size(14),
  fontWeight: 600,
  lineHeight: '110%',
  letterSpacing: theme.spacings.unit(-0.36),
}));

export const SrcsLabel = styled('p')(({ theme }) => ({
  color: theme.colors.primaryText,
  fontSize: theme.fonts.utility.size(7),
  margin: 0,
}));

export const SrcsContainer = styled('div')(({ theme }) => ({
  display: 'flex',
  flexDirection: 'row',
  width: '100%',
  alignItems: 'center',
  gap: theme.spacings.unit(3),
  marginBottom: theme.spacings.unit(4),
  flexWrap: 'wrap',
}));

export const SrcsImage = styled('div')(({ theme }) => ({
  position: 'relative',
  width: theme.spacings.unit(16),
  height: theme.spacings.unit(8),
}));

export const StyledDetailsLink = styled(StyledLink)(({ theme }) => ({
  fontWeight: 'bold',
  margin: `${theme.spacings.unit(8)} 0 ${theme.spacings.unit(6)}`,
  '@media print': {
    display: 'none',
  },
}));

export const Ellipses = styled('span')(({ theme }) => ({
  display: 'inline-block',
  color: theme.colors.secondaryText,
  fontSize: theme.fonts.utility.size(6),
  lineHeight: theme.fonts.utility.lineHeight(18 / 4),
  whiteSpace: 'break-spaces',
  '::before': {
    content: '"..."',
  },
  '@media print': {
    display: 'none',
  },
}));
